import { useState, useEffect } from 'react';
import {
  Modal,
  Fade,
  Backdrop,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress
} from '@material-ui/core';
import { Close, FileCopyOutlined, Facebook, Share, Twitter, WhatsApp } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { fetchReferralCode, fetchReferralStats } from '../../actions';
import { trackReferralFunnel } from '../../utils/tracking';

const buildFallbackShareLink = (referralCode) => {
  if (!referralCode) return null;
  const origin = window.location.origin || '';
  return `${origin}/login?ref=${encodeURIComponent(referralCode)}`;
};

const ShareEarnModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const [copied, setCopied] = useState(false);

  const referralState = useSelector((state) => state.referral);
  const referralPayload = referralState?.referralCode;
  const referralCode = referralPayload?.referralCode;
  const shareLink = referralPayload?.referralLink || buildFallbackShareLink(referralCode);
  const stats = referralState?.stats;
  const loading = referralState?.loading;
  const pendingRewardReferrals = stats?.pendingRewardReferrals || 0;
  const rewardedReferrals = stats?.totalReferrals || 0;
  const totalJoined = rewardedReferrals + pendingRewardReferrals;
  const rewardPlanConfigured = stats?.rewardPlanConfigured;
  // Reward durations come from backend config so copy stays true to what is granted.
  const rewardDays = stats?.rewardDaysPerReferral || 7;
  const refereeRewardDays = stats?.refereeRewardDays || 7;

  const shareMessage = `Check out QLearn — the best way to ace medical exams! Sign up: ${shareLink}`;
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  useEffect(() => {
    if (open) {
      trackReferralFunnel('referral_modal_opened');
    }
  }, [open]);

  useEffect(() => {
    if (open && !referralPayload) {
      dispatch(fetchReferralCode());
    }
    if (open && !stats) {
      dispatch(fetchReferralStats());
    }
  }, [open, referralPayload, stats, dispatch]);

  const handleCopy = async () => {
    if (!shareLink) return;
    await navigator.clipboard.writeText(shareLink);
    trackReferralFunnel('referral_link_copied');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleNativeShare = async () => {
    trackReferralFunnel('referral_share_clicked', { channel: 'native' });
    try {
      await navigator.share({ title: 'QLearn', text: shareMessage, url: shareLink });
    } catch (shareError) {
      // Dismissing the share sheet rejects with AbortError; nothing to recover.
      console.debug('Share sheet closed:', shareError?.message);
    }
  };

  const handleWhatsApp = () => {
    trackReferralFunnel('referral_share_clicked', { channel: 'whatsapp' });
    window.open(`https://wa.me/?text=${encodeURIComponent(shareMessage)}`, '_blank');
  };

  const handleFacebook = () => {
    trackReferralFunnel('referral_share_clicked', { channel: 'facebook' });
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink)}`, '_blank');
  };

  const handleTwitter = () => {
    trackReferralFunnel('referral_share_clicked', { channel: 'twitter' });
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}`, '_blank');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={open}>
        <Box style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          // Theme surface + text via the theme CSS vars (set on document.body by
          // setTheme) so this MUI portal isn't a stark white box in dark mode.
          // Light mode stays visually near-identical (surface #fff / text #000),
          // aside from the chip + secondary text picking up the app tokens.
          background: 'var(--background-color, #fff)',
          color: 'var(--text-color, #000)',
          border: '1px solid var(--border-color, transparent)',
          borderRadius: 8, padding: 24,
          minWidth: 320, maxWidth: 480, outline: 'none'
        }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            {/* B9: when the backend reports no reward plan is configured
                (rewardPlanConfigured === false), drop the "Free Pro" reward
                framing so we don't promise a reward we can't grant. The share
                UI + the "temporarily unavailable" notice still render. undefined
                (stats not yet loaded) keeps today's reward copy. */}
            <Typography variant="h6">
              {rewardPlanConfigured === false ? 'Share QLearn' : 'Share & Earn Free Pro'}
            </Typography>
            <IconButton size="small" aria-label="Close" onClick={onClose} style={{ color: 'var(--action-icon, rgba(0,0,0,0.54))' }}><Close fontSize="small" /></IconButton>
          </Box>

          {rewardPlanConfigured === false ? (
            <Typography variant="body2" style={{ marginBottom: 8, color: 'var(--sub-text, rgba(0,0,0,0.54))' }}>
              Invite friends who are preparing for their exams to join you on QLearn.
            </Typography>
          ) : (
            <Typography variant="body2" style={{ marginBottom: 8, color: 'var(--sub-text, rgba(0,0,0,0.54))' }}>
              Give friends <strong>{refereeRewardDays} days of Pro</strong> when they join with your link.
              When they finish their first quiz, you get <strong>{rewardDays} days of Pro free</strong>.
            </Typography>
          )}

          {totalJoined > 0 && (
            <Typography variant="caption" style={{ display: 'block', marginBottom: 12, color: 'var(--sub-text, rgba(0,0,0,0.54))' }}>
              {totalJoined} {totalJoined === 1 ? 'person' : 'people'} joined via your link
            </Typography>
          )}
          {pendingRewardReferrals > 0 && (
            <Typography variant="caption" style={{ display: 'block', marginBottom: 12, color: 'var(--sub-text, rgba(0,0,0,0.54))' }}>
              {pendingRewardReferrals} {pendingRewardReferrals === 1 ? 'reward unlocks' : 'rewards unlock'} when your friends finish their first quiz
            </Typography>
          )}
          {rewardPlanConfigured === false && (
            <Typography variant="caption" color="error" style={{ display: 'block', marginBottom: 12, color: 'var(--error-text, #c62828)' }}>
              Pro rewards are temporarily unavailable.
            </Typography>
          )}

          {loading && !shareLink ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : shareLink ? (
            <>
              <Box style={{
                display: 'flex', alignItems: 'center', gap: 8,
                // themed light-grey link chip -> readable in dark (list-bkg token)
                background: 'var(--list-bkg, #f5f5f5)', borderRadius: 4, padding: '6px 10px', marginBottom: 16
              }}>
                <Typography variant="caption" style={{ flex: 1, wordBreak: 'break-all' }}>
                  {shareLink}
                </Typography>
                {/* On the raised link chip (--list-bkg), the de-emphasized
                    --action-icon grey is only ~1.9:1 in dark. Use the chip's own
                    text color (matches the link text beside it) for an AA-legible,
                    tappable copy affordance in both themes. */}
                <IconButton size="small" aria-label="Copy link" onClick={handleCopy} style={{ color: 'var(--text-color, rgba(0,0,0,0.87))' }}>
                  <FileCopyOutlined fontSize="small" />
                </IconButton>
              </Box>
              {copied && (
                <Typography variant="caption" color="primary" style={{ display: 'block', marginBottom: 8, color: 'var(--list-item-sel-color, #3f51b5)' }}>
                  Copied!
                </Typography>
              )}
              <Box display="flex" gap={8} style={{ gap: 8, marginBottom: 8 }}>
                <Button
                  variant="contained"
                  style={{ background: '#25D366', color: '#fff', flex: 1 }}
                  startIcon={<WhatsApp />}
                  onClick={handleWhatsApp}
                >
                  WhatsApp
                </Button>
                {canNativeShare && (
                  <Button
                    variant="contained"
                    color="primary"
                    style={{ flex: 1 }}
                    startIcon={<Share />}
                    onClick={handleNativeShare}
                  >
                    Share…
                  </Button>
                )}
              </Box>
              <Box display="flex" gap={8} style={{ gap: 8 }}>
                <Button
                  variant="contained"
                  style={{ background: '#1877F2', color: '#fff', flex: 1 }}
                  startIcon={<Facebook />}
                  onClick={handleFacebook}
                >
                  Facebook
                </Button>
                <Button
                  variant="contained"
                  style={{ background: '#1DA1F2', color: '#fff', flex: 1 }}
                  startIcon={<Twitter />}
                  onClick={handleTwitter}
                >
                  Twitter / X
                </Button>
              </Box>
            </>
          ) : (
            <Typography variant="body2" color="error" style={{ color: 'var(--error-text, #c62828)' }}>Not able to Fetch Referral link</Typography>
          )}
        </Box>
      </Fade>
    </Modal>
  );
};

export default ShareEarnModal;
